import { useId, type ReactElement, type RefObject } from 'react';
import type { LandingGroup } from '@/lib/boards-page';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { Button } from '../Button';
import { EmptyState } from '../EmptyState';
import { BoardTile } from './BoardTile';

/**
 * One workspace's boards on the landing page — `FE-23.1`, chunk `D2`, agent E;
 * extended by `FE-08.1`, chunk `D3`, agent K.
 *
 * ---------------------------------------------------------------------------
 * THE "CREATE A BOARD" CONTROL, AND WHY IT COULD NOT BE RENDERED BEFORE NOW
 * ---------------------------------------------------------------------------
 * `entry-surfaces.md` **D4** ends the workspace-creation flow by moving focus
 * to this group's *Create a board* button. `D2` rendered no such button,
 * because its behaviour did not exist and `FR-EXP-034` forbids in as many
 * words *an action button they cannot use*; the `<h2>` carried `tabIndex={-1}`
 * as the interim focus target and this docstring named the seam.
 *
 * **`D3` closes it.** The button renders when `canCreateBoard`, and it — not
 * the heading — is where `focusRef` points, so the workspace-creation flow now
 * ends on the action `entry-surfaces.md` **D4** always meant it to end on.
 *
 * ⚠ **The heading keeps its `tabIndex={-1}`, deliberately.** `focusRef` falls
 * back to it for a group that draws no button (the §5.2 guest case), and a
 * `focus()` call on a heading with no `tabIndex` is a silent no-op that leaves
 * focus on `<body>` — the fallback has to be genuinely focusable or it is not
 * a fallback at all. It is not a tab stop either way (`-1`), so it costs the
 * `FR-EXP-022` trail nothing.
 *
 * The empty body still names the state and offers no control: an empty group's
 * *Create a board* is the one in its own header, three lines above it, and a
 * second copy would be two controls for one action.
 *
 * ⚠ **The heading is not a link.** `boardGroupSchema` carries no `shortName`
 * and no nested workspace object — deliberately, on `FR-PERM-023`, because
 * computing anything over a workspace's whole board set would leak boards a
 * single-board guest cannot see. There is nothing to link to, and a heading
 * that navigated to a page nobody built would be worse than text.
 *
 * ⚠ **`EmptyState` carries its own opaque sheet.** Do not wrap it in
 * `CardSurface` — a nested radius and a doubled shadow read as a styling bug
 * rather than as a composition one.
 */
export interface BoardGroupProps {
  group: LandingGroup;
  /**
   * Focused programmatically after this workspace is created — see above.
   *
   * ⚠ **Renamed from `headingRef` in `D3`, and the type widened with it**:
   * it now points at the *Create a board* button whenever this group draws
   * one, and at the `<h2>` only as the fallback. A ref named for the element
   * it no longer usually holds is the kind of stale claim this repository
   * treats as worse than no claim. `BoardsLanding` is the only caller.
   */
  focusRef?: RefObject<HTMLElement | null>;
  /**
   * `FE-08.1` — whether this group draws *Create a board*. Chunk `D3`.
   *
   * ⚠ **Not derivable from `group`.** `boardsPageSchema.groups` is built from
   * boards the caller can **see**, not workspaces they belong to: a guest with
   * one `board_members` row produces a group for a workspace they are not a
   * member of, and `POST /boards` calls `requireWorkspaceMember` and would
   * answer **404**. `FR-EXP-034` forbids an action button the member cannot
   * use. `routes/index.tsx` computes this from `useWorkspaces()`, which is
   * membership-scoped — see `D3-BRIEF.md` §5.2.
   */
  canCreateBoard: boolean;
  onCreateBoard: () => void;
  starredBoardIds?: ReadonlySet<string> | undefined;
  onToggleBoardStar?:
    ((board: LandingGroup['boards'][number], nextStarred: boolean) => void) | undefined;
  className?: string;
}

export function BoardGroup({
  group,
  focusRef,
  canCreateBoard,
  onCreateBoard,
  starredBoardIds = new Set(),
  onToggleBoardStar,
  className,
}: BoardGroupProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const headingId = useId();
  const workspaceName =
    group.workspaceName.trim() === ''
      ? t('boards.untitledWorkspace')
      : group.workspaceName;

  /*
   * A callback ref rather than passing `focusRef` straight through, because
   * the two candidate elements have different types: `Button`'s own `ref` is
   * `Ref<HTMLButtonElement>` and `RefObject<HTMLElement | null>` is not
   * assignable to it (`current` is mutable, so TypeScript will not widen it).
   * Writing through a callback is the one spelling that serves both elements
   * from one prop.
   */
  function setFocusTarget(node: HTMLElement | null) {
    if (focusRef) focusRef.current = node;
  }

  return (
    <section aria-labelledby={headingId} className={cn('flex flex-col gap-3', className)}>
      {/*
        The heading and its action on one row. `justify-between` rather than a
        gap-and-flow, so the button sits at the group's trailing edge and the
        row reads as `<workspace> ………… Create a board` at a glance — the same
        shape `ListColumn` uses for its own per-container create affordance.
      */}
      <div className="flex items-center justify-between gap-3">
        <h2
          id={headingId}
          ref={canCreateBoard ? undefined : setFocusTarget}
          tabIndex={-1}
          className="font-display text-lg font-semibold text-ink"
        >
          {workspaceName}
        </h2>
        {canCreateBoard && (
          /*
           * ⚠ **The accessible name BEGINS with the visible label** — WCAG
           * 2.5.3 (Label in Name), `D3-BRIEF.md` §7, and the same shape
           * `ListColumn`'s "Add a card" / "Add a card to Backlog" pair
           * already ships. A voice-control user saying *"create a board"*
           * still matches this control; a name spelled the other way round
           * ("Engineering: create a board") would not.
           *
           * A per-container create affordance is not the page's primary
           * action, and a **filled** button per group would make the landing
           * page a wall of accents — which is why this is not `primary`. It
           * was `quiet` until the screenshot pass; see the note on the
           * `variant` below for what that looked like.
           */
          <Button
            ref={setFocusTarget}
            /*
             * ⚠ **`secondary`, not `quiet`, and the screenshot pass is why.**
             * `quiet` was right by every automated gate — correct role,
             * correct accessible name, and the reasoning above about not
             * making the page a wall of accents is sound. At 1440×900 it
             * rendered as **plain text beside the heading**, with no boundary
             * at all until hover: the page's primary action, indistinguishable
             * from a label. Role and accessible-name queries cannot see that,
             * which is the whole reason `codex-handoff.md` §8 exists.
             *
             * `secondary` keeps the objection intact — no accent fill, so no
             * wall of accents — and gives the control a visible edge.
             */
            variant="secondary"
            size="sm"
            className="shrink-0"
            aria-label={t('boards.createBoardInWorkspace', {
              workspace: workspaceName,
            })}
            onClick={onCreateBoard}
          >
            {t('boards.createBoard')}
          </Button>
        )}
      </div>
      {/*
        ⚠ **The empty body is a grid CELL, not a full-width panel, and the
        screenshot pass is what decided that.**

        Rendered as a sibling of the grid it spanned the group's whole width
        (848px at 1440), centred, on a raised sheet — so on a page with three
        workspaces the **emptiest** one was the largest and most prominent
        thing on screen, louder than the workspaces that actually had boards.
        `EmptyState`'s title is `text-lg`, the same size as the `<h2>` above
        it, which is what made a rank-3 heading outweigh its rank-2 parent.

        Nothing was red: the copy was right, the roles were right, and every
        assertion passed. It is the same class C2 caught — *padding that
        inverts the page's emphasis* — and the product tie-breaker settles it:
        where a feature makes the page harder to read at a glance, the feature
        loses.

        Inside the grid it occupies one track, so it reads as a note sitting
        exactly where the first board would be, at a board's weight.
      */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
        {group.boards.length > 0 ? (
          group.boards.map((board) => (
            <BoardTile
              key={board.id}
              board={board}
              starred={starredBoardIds.has(board.id)}
              onToggleStar={onToggleBoardStar}
            />
          ))
        ) : (
          <EmptyState
            tone="neutral"
            size="inline"
            headingLevel={3}
            title={t('boards.groupEmpty')}
          />
        )}
      </div>
    </section>
  );
}
